"use client";

import React, { Suspense } from "react";
import { useQueryParam } from "@/lib/routing";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/components/ui/tabs";
import {
  Youtube as YoutubeIcon,
  Instagram as InstagramIcon,
  Video as VideoIcon,
  Globe as GlobeIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  Copy as CopyIcon,
} from "lucide-react";
import BatchImageDownloader from "@/app/components/BatchImageDownloader";
import BulkMediaDownloader from "@/app/components/BulkMediaDownloader";
import SiteCopier from "@/app/components/SiteCopier";
import { YouTubeDownloader } from "@/app/components/YouTubeDownloader";
import { InstagramDownloader } from "@/app/components/InstagramDownloader";
import { TikTokDownloader } from "@/app/components/TikTokDownloader";
import { GeneralDownloader } from "@/app/components/GeneralDownloader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";

const TABS = ["youtube", "instagram", "tiktok", "general", "batch-image", "bulk", "clone"] as const;
type TabValue = (typeof TABS)[number];

const OVERVIEW_ITEMS: { id: TabValue; icon: React.ElementType; title: string; description: string }[] = [
  { id: "youtube", icon: YoutubeIcon, title: "YouTube", description: "Download videos and audio from YouTube" },
  { id: "instagram", icon: InstagramIcon, title: "Instagram", description: "Download photos and reels from Instagram" },
  { id: "tiktok", icon: VideoIcon, title: "TikTok", description: "Download videos from TikTok" },
  { id: "general", icon: GlobeIcon, title: "General", description: "Download from any supported URL" },
  { id: "batch-image", icon: ImageIcon, title: "Batch Image", description: "Download multiple images as a zip" },
  { id: "bulk", icon: DownloadIcon, title: "Bulk Media", description: "Download multiple media files" },
  { id: "clone", icon: CopyIcon, title: "Site Copier", description: "Fetch page HTML, images, scripts & styles" },
];

function DownloadPageContent() {
  const [activeTab, setActiveTab] = useQueryParam<TabValue>("tab", {
    parse: (raw) => (raw ?? "youtube") as TabValue,
    serialize: (v) => v ?? null,
    defaultValue: "youtube",
    replace: true,
  });

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold tracking-tight mb-2">Downloaders</h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Download videos, images, and media from YouTube, Instagram, TikTok, and more
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)} className="space-y-6">
          <TabsList className="flex w-full flex-wrap gap-2 p-2 rounded-xl bg-muted/60 h-auto min-h-12 [&>button]:shrink-0 [&>button]:rounded-lg [&>button]:data-[state=active]:bg-background [&>button]:data-[state=active]:shadow-sm">
            {OVERVIEW_ITEMS.map(({ id, icon: Icon, title }) => (
              <TabsTrigger key={id} value={id} className="flex items-center gap-2 px-4 py-2">
                <Icon className="h-4 w-4 shrink-0" />
                <span>{title}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="min-h-[320px]">
          {/* ── YOUTUBE DOWNLOADER TAB ─────────────────────────────────────── */}
          <TabsContent value="youtube" className="mt-0 animate-in fade-in-0 duration-200">
            <YouTubeDownloader />
          </TabsContent>

          {/* ── INSTAGRAM DOWNLOADER TAB ─────────────────────────────────────── */}
          <TabsContent value="instagram" className="mt-0 animate-in fade-in-0 duration-200">
            <InstagramDownloader />
          </TabsContent>

          {/* ── TIKTOK DOWNLOADER TAB ─────────────────────────────────────── */}
          <TabsContent value="tiktok" className="mt-0 animate-in fade-in-0 duration-200">
            <TikTokDownloader />
          </TabsContent>

          {/* ── GENERAL DOWNLOADER TAB ─────────────────────────────────────── */}
          <TabsContent value="general" className="mt-0 animate-in fade-in-0 duration-200">
            <GeneralDownloader />
          </TabsContent>

          {/* ── BATCH IMAGE TAB ───────────────────────────────────────────── */}
          <TabsContent value="batch-image" className="mt-0 animate-in fade-in-0 duration-200">
            <Card className="w-full card-enhanced">
              <CardHeader>
                <CardTitle>Batch Image Download</CardTitle>
                <CardDescription>Enter image names (one per line) and download them as a zip.</CardDescription>
              </CardHeader>
              <CardContent>
                <BatchImageDownloader />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── BULK MEDIA TAB ─────────────────────────────────────────────── */}
          <TabsContent value="bulk" className="mt-0 animate-in fade-in-0 duration-200">
            <Card className="w-full card-enhanced">
              <CardHeader>
                <CardTitle>Bulk Media Download</CardTitle>
                <CardDescription>Download multiple media files from various sources.</CardDescription>
              </CardHeader>
              <CardContent>
                <BulkMediaDownloader />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── SITE COPIER TAB ─────────────────────────────────────────────── */}
          <TabsContent value="clone" className="mt-0 animate-in fade-in-0 duration-200">
            <Card className="w-full card-enhanced">
              <CardHeader>
                <CardTitle>Site Copier</CardTitle>
                <CardDescription>Fetch a page's HTML, images, scripts & styles into a ZIP.</CardDescription>
              </CardHeader>
              <CardContent>
                <SiteCopier />
              </CardContent>
            </Card>
          </TabsContent>
          </div>
        </Tabs>

        <Card className="card-enhanced mt-8 rounded-xl overflow-hidden">
          <CardHeader>
            <CardTitle>All Downloaders</CardTitle>
            <CardDescription>
              Click a card to switch to that downloader
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {OVERVIEW_ITEMS.map(({ id, icon: Icon, title, description }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`text-left p-4 rounded-xl border transition-all duration-200 flex flex-col items-center sm:items-start gap-3 hover:border-primary/40 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    activeTab === id
                      ? "border-primary/50 bg-primary/5 ring-1 ring-primary/20"
                      : "border-border bg-muted/30"
                  }`}
                >
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
                    style={{ color: activeTab === id ? "#2563eb" : undefined }}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm">{title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{description}</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function DownloadPage() {
  return (
    <Suspense fallback={<div className="container mx-auto px-4 py-8">Loading...</div>}>
      <DownloadPageContent />
    </Suspense>
  );
}
